import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuthStore } from '../../stores';

interface CreditTransaction {
  id: number;
  amount: number;
  transactionType: string;
  description: string;
  createdAt: string;
}

interface UserMovie {
  id: number;
  name: string;
  description: string;
  width: number;
  height: number;
  fps: number;
  sceneCount: number;
}

interface UserPreference {
  id: number;
  componentName: string;
  preferenceKey: string;
  preferenceValue: unknown;
  metadata?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface UserNotification {
  notificationId: string;
  type: string;
  title: string;
  message: string;
  createdAt: string;
  isRead: boolean;
}

interface UserConnection {
  connectionId: string;
  userAgent: string;
  connected: boolean;
  connectedAt?: string | null;
  disconnectedAt?: string | null;
}

interface UserActivity {
  id: string;
  type: string;
  action: string;
  details?: string | null;
  ipAddress?: string | null;
  createdAt: string;
}

interface ExternalLogin {
  loginProvider: string;
  providerKey: string;
  providerDisplayName?: string | null;
}

interface UserDetailData {
  id: string;
  email: string;
  handle: string;
  name: string;
  surname: string;
  fullName: string;
  phoneNumber?: string | null;
  emailConfirmed: boolean;
  lockoutEnabled: boolean;
  lockoutEnd?: string | null;
  accessFailedCount: number;
  twoFactorEnabled: boolean;
  hasPassword: boolean;
  roles: string[];
  role: string;
  refreshTokenExpiryTime?: string | null;
  externalLogins: ExternalLogin[];
  credits: {
    currentCredits: number;
    totalEarned: number;
    totalSpent: number;
    transactions: CreditTransaction[];
  };
  movies: UserMovie[];
  preferences: UserPreference[];
  notifications: UserNotification[];
  connections: UserConnection[];
  recentActivity: UserActivity[];
}

function formatDate(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function formatJson(value: unknown) {
  if (value == null) return '—';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function UserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const { accessToken, user: currentUser } = useAuthStore();
  const [data, setData] = useState<UserDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [roles, setRoles] = useState<string[]>([]);
  const [roleBusy, setRoleBusy] = useState(false);
  const [creditAmount, setCreditAmount] = useState('100');
  const [creditDescription, setCreditDescription] = useState('');
  const [creditBusy, setCreditBusy] = useState(false);
  const [creditMessage, setCreditMessage] = useState('');

  const loadDetail = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? 'Kullanıcı detayı alınamadı');
      }
      setData((await response.json()) as UserDetailData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kullanıcı detayı alınamadı');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [accessToken, userId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const response = await fetch('/api/admin/roles', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) return;
        setRoles((await response.json()) as string[]);
      } catch {
        // ignore
      }
    };
    void loadRoles();
  }, [accessToken]);

  const handleRoleChange = async (newRole: string) => {
    if (!data || data.id === currentUser?.id) return;
    setRoleBusy(true);
    setError('');
    try {
      const response = await fetch(`/api/admin/users/${data.id}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ role: newRole }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? 'Rol güncellenemedi');
      }
      setData((prev) =>
        prev
          ? {
              ...prev,
              role: newRole,
              roles: [newRole, ...prev.roles.filter((r) => r !== newRole)],
            }
          : prev,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Rol güncellenemedi');
    } finally {
      setRoleBusy(false);
    }
  };

  const handleGrantCredits = async (e: FormEvent) => {
    e.preventDefault();
    if (!data) return;
    const amount = Number.parseInt(creditAmount, 10);
    if (!Number.isFinite(amount) || amount <= 0) {
      setCreditMessage('Geçerli bir kredi miktarı girin.');
      return;
    }

    setCreditBusy(true);
    setCreditMessage('');
    try {
      const response = await fetch(`/api/admin/users/${data.id}/credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          amount,
          description: creditDescription.trim() || undefined,
        }),
      });
      const body = (await response.json().catch(() => null)) as {
        message?: string;
        currentCredits?: number;
        transactions?: CreditTransaction[];
      } | null;
      if (!response.ok) {
        throw new Error(body?.message ?? 'Kredi eklenemedi');
      }

      setData((prev) => {
        if (!prev) return prev;
        const transactions = body?.transactions ?? prev.credits.transactions;
        const earned = transactions.reduce((sum, t) => (t.amount > 0 ? sum + t.amount : sum), 0);
        const spent = transactions.reduce(
          (sum, t) => (t.amount < 0 ? sum + Math.abs(t.amount) : sum),
          0,
        );
        return {
          ...prev,
          credits: {
            currentCredits: body?.currentCredits ?? prev.credits.currentCredits + amount,
            totalEarned: earned,
            totalSpent: spent,
            transactions,
          },
        };
      });
      setCreditMessage(body?.message ?? 'Kredi eklendi.');
      setCreditDescription('');
      await loadDetail();
    } catch (err) {
      setCreditMessage(err instanceof Error ? err.message : 'Kredi eklenemedi');
    } finally {
      setCreditBusy(false);
    }
  };

  if (loading) {
    return <div className="admin-loading">Yükleniyor...</div>;
  }

  if (!data) {
    return (
      <div className="admin-page">
        <div className="admin-error">{error || 'Kullanıcı bulunamadı'}</div>
        <Link to="/admin/users" className="admin-btn admin-btn-secondary">
          ← Kullanıcılara dön
        </Link>
      </div>
    );
  }

  const liveConnections = data.connections.filter((c) => c.connected).length;

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div>
          <p className="admin-breadcrumb">
            <Link to="/admin/users">Users</Link>
            <span aria-hidden="true"> / </span>
            <span>{data.fullName || data.email}</span>
          </p>
          <h1>{data.fullName || data.email}</h1>
          <p>{data.email}</p>
        </div>
        <Link to="/admin/users" className="admin-btn admin-btn-secondary">
          ← Listeye dön
        </Link>
      </header>

      {error && <div className="admin-error">{error}</div>}

      <section className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="admin-stat-content">
            <div className="admin-stat-value">{data.credits.currentCredits.toLocaleString()}</div>
            <div className="admin-stat-label">Kredi bakiyesi</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-content">
            <div className="admin-stat-value">{data.movies.length}</div>
            <div className="admin-stat-label">Film</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-content">
            <div className="admin-stat-value">{liveConnections}</div>
            <div className="admin-stat-label">Canlı bağlantı</div>
          </div>
        </div>
        <div className="admin-stat-card">
          <div className="admin-stat-content">
            <div className="admin-stat-value">{data.role}</div>
            <div className="admin-stat-label">Rol</div>
          </div>
        </div>
      </section>

      <div className="admin-dashboard-grid">
        <section className="admin-card admin-dashboard-panel">
          <div className="admin-card-header">
            <h2>Profil</h2>
          </div>
          <div className="admin-card-body">
            <dl className="admin-detail-list">
              <div>
                <dt>ID</dt>
                <dd className="admin-mono">{data.id}</dd>
              </div>
              <div>
                <dt>Handle</dt>
                <dd>{data.handle || '—'}</dd>
              </div>
              <div>
                <dt>Ad / Soyad</dt>
                <dd>
                  {data.name || '—'} {data.surname || ''}
                </dd>
              </div>
              <div>
                <dt>Telefon</dt>
                <dd>{data.phoneNumber || '—'}</dd>
              </div>
              <div>
                <dt>Email confirmed</dt>
                <dd>{data.emailConfirmed ? 'Evet' : 'Hayır'}</dd>
              </div>
              <div>
                <dt>Password</dt>
                <dd>{data.hasPassword ? 'Var' : 'Yok (social-only olabilir)'}</dd>
              </div>
              <div>
                <dt>2FA</dt>
                <dd>{data.twoFactorEnabled ? 'Açık' : 'Kapalı'}</dd>
              </div>
              <div>
                <dt>Lockout</dt>
                <dd>
                  {data.lockoutEnabled ? 'Enabled' : 'Disabled'}
                  {data.lockoutEnd ? ` · end ${formatDate(data.lockoutEnd)}` : ''}
                </dd>
              </div>
              <div>
                <dt>Access failed</dt>
                <dd>{data.accessFailedCount}</dd>
              </div>
              <div>
                <dt>Refresh token expiry</dt>
                <dd>{formatDate(data.refreshTokenExpiryTime)}</dd>
              </div>
              <div>
                <dt>Roller</dt>
                <dd>{data.roles.join(', ') || data.role}</dd>
              </div>
            </dl>

            <div className="admin-detail-actions">
              <label className="admin-form-group">
                <span>Rol değiştir</span>
                <select
                  className="admin-setting-select"
                  value={data.role}
                  disabled={roleBusy || data.id === currentUser?.id}
                  onChange={(e) => void handleRoleChange(e.target.value)}
                >
                  {(roles.length > 0 ? roles : [data.role]).map((role) => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>
        </section>

        <section className="admin-card admin-dashboard-panel">
          <div className="admin-card-header">
            <h2>Krediler</h2>
          </div>
          <div className="admin-card-body">
            <div className="admin-inline-stats">
              <div>
                <strong>{data.credits.currentCredits.toLocaleString()}</strong>
                <span>Bakiye</span>
              </div>
              <div>
                <strong>{data.credits.totalEarned.toLocaleString()}</strong>
                <span>Kazanılan</span>
              </div>
              <div>
                <strong>{data.credits.totalSpent.toLocaleString()}</strong>
                <span>Harcanan</span>
              </div>
            </div>

            <form className="admin-inline-form" onSubmit={(e) => void handleGrantCredits(e)}>
              <div className="admin-form-row">
                <label className="admin-form-group">
                  <span>Kredi ekle</span>
                  <input
                    className="admin-form-input"
                    type="number"
                    min={1}
                    value={creditAmount}
                    onChange={(e) => setCreditAmount(e.target.value)}
                    disabled={creditBusy}
                  />
                </label>
                <label className="admin-form-group">
                  <span>Açıklama</span>
                  <input
                    className="admin-form-input"
                    type="text"
                    value={creditDescription}
                    onChange={(e) => setCreditDescription(e.target.value)}
                    placeholder="Opsiyonel"
                    disabled={creditBusy}
                  />
                </label>
              </div>
              <button type="submit" className="admin-btn admin-btn-primary" disabled={creditBusy}>
                {creditBusy ? 'Ekleniyor…' : 'Kredi ver'}
              </button>
              {creditMessage && <p className="admin-form-hint">{creditMessage}</p>}
            </form>

            <div className="admin-table-container admin-table-compact">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Tip</th>
                    <th>Tutar</th>
                    <th>Açıklama</th>
                  </tr>
                </thead>
                <tbody>
                  {data.credits.transactions.length === 0 ? (
                    <tr>
                      <td colSpan={4}>İşlem yok</td>
                    </tr>
                  ) : (
                    data.credits.transactions.map((tx) => (
                      <tr key={tx.id}>
                        <td>{formatDate(tx.createdAt)}</td>
                        <td>{tx.transactionType}</td>
                        <td className={tx.amount >= 0 ? 'admin-positive' : 'admin-negative'}>
                          {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
                        </td>
                        <td>{tx.description}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      <section className="admin-card admin-card-spaced">
        <div className="admin-card-header">
          <h2>External logins</h2>
        </div>
        <div className="admin-card-body">
          {data.externalLogins.length === 0 ? (
            <p className="admin-empty">Bağlı social login yok.</p>
          ) : (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Display</th>
                    <th>Key</th>
                  </tr>
                </thead>
                <tbody>
                  {data.externalLogins.map((login) => (
                    <tr key={`${login.loginProvider}-${login.providerKey}`}>
                      <td>{login.loginProvider}</td>
                      <td>{login.providerDisplayName || '—'}</td>
                      <td className="admin-mono">{login.providerKey}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className="admin-card admin-card-spaced">
        <div className="admin-card-header">
          <h2>Filmler ({data.movies.length})</h2>
        </div>
        <div className="admin-card-body admin-card-body-flush">
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Ad</th>
                  <th>Boyut</th>
                  <th>FPS</th>
                  <th>Sahne</th>
                </tr>
              </thead>
              <tbody>
                {data.movies.length === 0 ? (
                  <tr>
                    <td colSpan={5}>Film yok</td>
                  </tr>
                ) : (
                  data.movies.map((movie) => (
                    <tr key={movie.id}>
                      <td>{movie.id}</td>
                      <td>
                        <div>{movie.name}</div>
                        {movie.description && (
                          <div className="admin-muted-text">{movie.description}</div>
                        )}
                      </td>
                      <td>
                        {movie.width}×{movie.height}
                      </td>
                      <td>{movie.fps}</td>
                      <td>{movie.sceneCount}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <div className="admin-dashboard-grid">
        <section className="admin-card admin-dashboard-panel">
          <div className="admin-card-header">
            <h2>Tercihler ({data.preferences.length})</h2>
          </div>
          <div className="admin-card-body admin-card-body-flush">
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Key</th>
                    <th>Value</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {data.preferences.length === 0 ? (
                    <tr>
                      <td colSpan={4}>Tercih yok</td>
                    </tr>
                  ) : (
                    data.preferences.map((pref) => (
                      <tr key={pref.id}>
                        <td>{pref.componentName}</td>
                        <td>{pref.preferenceKey}</td>
                        <td className="admin-mono">{formatJson(pref.preferenceValue)}</td>
                        <td>{formatDate(pref.updatedAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="admin-card admin-dashboard-panel">
          <div className="admin-card-header">
            <h2>Bildirimler ({data.notifications.length})</h2>
          </div>
          <div className="admin-card-body admin-card-body-flush">
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Tip</th>
                    <th>Başlık</th>
                    <th>Okundu</th>
                    <th>Tarih</th>
                  </tr>
                </thead>
                <tbody>
                  {data.notifications.length === 0 ? (
                    <tr>
                      <td colSpan={4}>Bildirim yok</td>
                    </tr>
                  ) : (
                    data.notifications.map((n) => (
                      <tr key={n.notificationId}>
                        <td>{n.type}</td>
                        <td>
                          <div>{n.title}</div>
                          <div className="admin-muted-text">{n.message}</div>
                        </td>
                        <td>{n.isRead ? 'Evet' : 'Hayır'}</td>
                        <td>{formatDate(n.createdAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>

      <div className="admin-dashboard-grid">
        <section className="admin-card admin-dashboard-panel">
          <div className="admin-card-header">
            <h2>Bağlantılar ({data.connections.length})</h2>
          </div>
          <div className="admin-card-body admin-card-body-flush">
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Durum</th>
                    <th>Connection</th>
                    <th>User-Agent</th>
                    <th>Connected</th>
                  </tr>
                </thead>
                <tbody>
                  {data.connections.length === 0 ? (
                    <tr>
                      <td colSpan={4}>Kayıt yok</td>
                    </tr>
                  ) : (
                    data.connections.map((c) => (
                      <tr key={c.connectionId}>
                        <td>{c.connected ? 'Online' : 'Offline'}</td>
                        <td className="admin-mono">{c.connectionId}</td>
                        <td>{c.userAgent || '—'}</td>
                        <td>{formatDate(c.connectedAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="admin-card admin-dashboard-panel">
          <div className="admin-card-header">
            <h2>İlgili admin aktivite</h2>
          </div>
          <div className="admin-card-body admin-card-body-flush">
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Action</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentActivity.length === 0 ? (
                    <tr>
                      <td colSpan={3}>Aktivite yok</td>
                    </tr>
                  ) : (
                    data.recentActivity.map((a) => (
                      <tr key={a.id}>
                        <td>{formatDate(a.createdAt)}</td>
                        <td>{a.action}</td>
                        <td>{a.details || '—'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
